import { cloneElement, useState } from 'react';

import { Box, ClickAwayListener, IconButton, TableCell, TableCellProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

import { DateEditControl } from 'components/Table/EditControls/DateEditControl';
import { SelectEditControl } from 'components/Table/EditControls/SelectEditControl';
import { TextEditControl } from 'components/Table/EditControls/TextEditControl';

import { ColumnDataTypes } from '../../Table.types';

interface EditableCellProps extends Omit<TableCellProps, 'datatype'> {
  value: string;
  columnId: string;
  dataType: ColumnDataTypes;
  selectableValuesForEnums?: Array<string>;
  onCellEdit: (columnId: string, value: any) => Promise<any>;
  editControl?: JSX.Element;
}

const useEditableCellStyles = makeStyles(() => ({
  closeButton: {
    marginLeft: 8,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 0,
    paddingRight: 0,

    '& input': {
      fontSize: '14.375px',
    },
  },
  editButton: {
    marginRight: 16,
  },
}));

export const EditableCell = ({
  value,
  dataType,
  columnId,
  editControl,
  selectableValuesForEnums,
  onCellEdit,
  ...cellProps
}: EditableCellProps) => {
  const classes = useEditableCellStyles();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);

  const handleCellEdit = async value => {
    setIsSaving(true);
    setError(false);
    try {
      await onCellEdit(columnId, value);
      setIsEditing(false);
    } catch (e) {
      setError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const renderEditControl = (value): JSX.Element => {
    if (editControl) {
      return cloneElement(editControl, {
        value,
        onSubmitChange: handleCellEdit,
      });
    }

    switch (dataType) {
      case 'date':
      case 'datetime':
        return (
          <DateEditControl
            isInvalid={error}
            isSaving={isSaving}
            type={dataType}
            initialValue={value}
            onSubmitChange={handleCellEdit}
            onKeyDown={handleKeyDown}
          />
        );
      case 'enum':
        return (
          <SelectEditControl
            initialValue={value}
            isInvalid={error}
            isSaving={isSaving}
            onSubmitChange={handleCellEdit}
            values={selectableValuesForEnums ?? []}
            type="text"
          />
        );
      default:
        return (
          <TextEditControl
            isInvalid={error}
            isSaving={isSaving}
            type={dataType}
            initialValue={value}
            onSubmitChange={handleCellEdit}
            onKeyDown={handleKeyDown}
          />
        );
    }
  };

  return (
    <ClickAwayListener onClickAway={() => dataType === 'enum' || setIsEditing(false)}>
      <TableCell {...cellProps} style={{ padding: isEditing && '0 8px' }}>
        {isEditing ? (
          <Box className={classes.container}>
            {renderEditControl(value)}
            <IconButton
              className={classes.closeButton}
              disableFocusRipple={true}
              disableRipple
              onClick={() => setIsEditing(false)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box className={classes.container}>
            {value}
            <IconButton
              disableFocusRipple={true}
              disableRipple
              className={clsx('actionBtnSmall', classes.editButton)}
              onClick={() => setIsEditing(true)}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </TableCell>
    </ClickAwayListener>
  );
};
